<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="loading">
      <v-row class="mb-2">
        <v-col v-for="n in 4" :key="n" cols="6" md="3">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="image" height="260" class="mb-4" />
      <v-skeleton-loader type="table" />
    </template>

    <template v-else>
      <v-row class="mb-2">
        <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" md="3">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-caption grey--text text--darken-1">{{ kpi.label }}</div>
            <div class="text-h6 font-weight-bold mt-1">{{ kpi.value }}</div>
          </v-card>
        </v-col>
        <v-col v-if="kpis.length === 0" cols="12">
          <empty-state icon="mdi-chart-box-outline" :title="$t('finance_no_kpi_data')" />
        </v-col>
      </v-row>

      <v-card outlined class="pa-4 mb-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">{{ $t("finance_charts_label") }}</div>

        <v-tabs v-model="activeChart" dense>
          <v-tab value="hours">{{ $t("finance_by_hour") }}</v-tab>
          <v-tab value="days">{{ $t("finance_by_day") }}</v-tab>
          <v-tab value="months">{{ $t("finance_by_month") }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeChart">
          <v-tab-item value="hours">
            <apexchart
              v-if="hourlyTurnover.length"
              type="bar"
              height="280"
              :options="chartOptions(hourlyTurnover)"
              :series="chartSeries(hourlyTurnover)"
            />
            <empty-state v-else icon="mdi-chart-bar" :title="$t('finance_no_data')" />
          </v-tab-item>
          <v-tab-item value="days">
            <apexchart
              v-if="dailyTurnover.length"
              type="bar"
              height="280"
              :options="chartOptions(dailyTurnover)"
              :series="chartSeries(dailyTurnover)"
            />
            <empty-state v-else icon="mdi-chart-bar" :title="$t('finance_no_data')" />
          </v-tab-item>
          <v-tab-item value="months">
            <apexchart
              v-if="monthlyTurnover.length"
              type="bar"
              height="280"
              :options="chartOptions(monthlyTurnover)"
              :series="chartSeries(monthlyTurnover)"
            />
            <empty-state v-else icon="mdi-chart-bar" :title="$t('finance_no_data')" />
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <v-card outlined class="pa-4 mb-4">
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
          <div class="text-subtitle-2 font-weight-bold">{{ $t("finance_daily_overview_title") }}</div>
          <v-spacer />
          <dropdown
            v-model="breakdownPeriod"
            :items="breakdownPeriodOptions"
            item-text="label"
            item-value="value"
            hide-details
            style="max-width: 220px"
          />
        </div>

        <template v-if="device.isMobile">
          <mobile-data-cards
            :headers="dailyBreakdownHeaders"
            :items="filteredDailyBreakdown"
            item-key="date"
            :fields-per-row="2"
            empty-icon="mdi-calendar-blank-outline"
            :empty-title="$t('finance_no_data_to_display')"
          >
            <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
            <template v-slot:item.turnover="{ item }">{{ formatCurrency(item.turnover) }}</template>
            <template v-slot:item.delivery="{ item }">{{ formatCurrency(item.delivery) }}</template>
            <template v-slot:item.average="{ item }">
              {{ item.orders > 0 ? formatCurrency(item.turnover / item.orders) : "—" }}
            </template>
          </mobile-data-cards>
        </template>
        <template v-else>
          <v-data-table
            v-if="filteredDailyBreakdown.length > 0"
            :headers="dailyBreakdownHeaders"
            :items="filteredDailyBreakdown"
            item-key="date"
            class="striped-table"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
            <template v-slot:item.turnover="{ item }">{{ formatCurrency(item.turnover) }}</template>
            <template v-slot:item.delivery="{ item }">{{ formatCurrency(item.delivery) }}</template>
            <template v-slot:item.average="{ item }">
              {{ item.orders > 0 ? formatCurrency(item.turnover / item.orders) : "—" }}
            </template>
          </v-data-table>
          <empty-state v-else icon="mdi-calendar-blank-outline" :title="$t('finance_no_data_to_display')" />
        </template>
      </v-card>

      <v-card outlined class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">{{ $t("finance_comparison_title") }}</div>

        <div class="compare-row">
          <div class="compare-side">
            <p class="compare-label">{{ $t("finance_this_month") }}</p>
            <p class="compare-value">{{ formatCurrency(comparePeriod.current) }}</p>
          </div>
          <v-icon size="18" color="grey">mdi-arrow-left-right</v-icon>
          <div class="compare-side">
            <p class="compare-label">{{ $t("finance_last_month") }}</p>
            <p class="compare-value">{{ formatCurrency(comparePeriod.previous) }}</p>
          </div>
          <v-chip :color="periodDelta >= 0 ? 'success' : 'error'" outlined small>
            <v-icon left small>{{ periodDelta >= 0 ? "mdi-trending-up" : "mdi-trending-down" }}</v-icon>
            {{ periodDelta >= 0 ? "+" : "" }}{{ periodDelta.toFixed(1) }}%
          </v-chip>
        </div>

        <v-divider class="my-3" />

        <div class="compare-row">
          <div class="compare-side">
            <p class="compare-label">{{ $t("finance_this_year") }}</p>
            <p class="compare-value">{{ formatCurrency(compareYear.current) }}</p>
          </div>
          <v-icon size="18" color="grey">mdi-arrow-left-right</v-icon>
          <div class="compare-side">
            <p class="compare-label">{{ $t("finance_last_year") }}</p>
            <p class="compare-value">{{ formatCurrency(compareYear.previous) }}</p>
          </div>
          <v-chip :color="yearDelta >= 0 ? 'success' : 'error'" outlined small>
            <v-icon left small>{{ yearDelta >= 0 ? "mdi-trending-up" : "mdi-trending-down" }}</v-icon>
            {{ yearDelta >= 0 ? "+" : "" }}{{ yearDelta.toFixed(1) }}%
          </v-chip>
        </div>
      </v-card>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import gpsApi from "@/plugins/gpsApi";

export default {
  name: "FinanceTabTurnover",

  components: { apexchart: VueApexCharts, EmptyState, MobileDataCards },

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    kpis: [],
    hourlyTurnover: [],
    dailyTurnover: [],
    monthlyTurnover: [],
    dailyBreakdown: [],
    comparePeriod: { current: 0, previous: 0 },
    compareYear: { current: 0, previous: 0 },

    activeChart: "days",
    breakdownPeriod: "all",
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant && this.$store.state.restaurant.id;
    },
    breakdownPeriodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_7_days"), value: "7d" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
      ];
    },
    dailyBreakdownHeaders() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_order_count"), value: "orders", align: "end" },
        { text: this.$t("finance_turnover"), value: "turnover", align: "end" },
        { text: this.$t("finance_delivery"), value: "delivery", align: "end" },
        { text: this.$t("finance_average"), value: "average", align: "end" },
      ];
    },
    periodDelta() {
      return this.comparePeriod.previous
        ? ((this.comparePeriod.current - this.comparePeriod.previous) / this.comparePeriod.previous) * 100
        : 0;
    },
    yearDelta() {
      return this.compareYear.previous
        ? ((this.compareYear.current - this.compareYear.previous) / this.compareYear.previous) * 100
        : 0;
    },
    filteredDailyBreakdown() {
      if (this.breakdownPeriod === "all") return this.dailyBreakdown;
      const days = { "7d": 7, "30d": 30, "90d": 90 }[this.breakdownPeriod];
      const now = Date.now();
      return this.dailyBreakdown.filter(
        (row) => now - new Date(row.date).getTime() <= days * 24 * 60 * 60 * 1000
      );
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const res = await gpsApi.get(`/restaurants/${this.restaurantId}/turnover`);
        const data = (res.data && res.data.data) || {};
        this.kpis = data.kpis || [];
        this.hourlyTurnover = data.hourly_turnover || [];
        this.dailyTurnover = data.daily_turnover || [];
        this.monthlyTurnover = data.monthly_turnover || [];
        this.dailyBreakdown = data.daily_breakdown || [];
        this.comparePeriod = data.compare_period || { current: 0, previous: 0 };
        this.compareYear = data.compare_year || { current: 0, previous: 0 };
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_turnover_load_error"));
      } finally {
        this.loading = false;
      }
    },
    chartOptions(points) {
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#00b37e"],
        dataLabels: { enabled: false },
        xaxis: { categories: points.map((p) => p.label) },
        yaxis: { labels: { formatter: (v) => this.formatCurrency(v) } },
        tooltip: { y: { formatter: (v) => this.formatCurrency(v) } },
        grid: { borderColor: "#f1f1f1" },
      };
    },
    chartSeries(points) {
      return [{ name: this.$t("finance_turnover_series_label"), data: points.map((p) => p.value) }];
    },
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString("sr-RS")} ${this.restaurantCurrency}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(`${dateStr}T00:00:00`);
      return d.toLocaleDateString("sr-RS", { year: "numeric", month: "2-digit", day: "2-digit" });
    },
  },
};
</script>

<style scoped>
.compare-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.compare-side {
  flex: 1;
  min-width: 100px;
}

.compare-label {
  margin: 0 0 2px;
  font-size: 0.75rem;
  color: #9aa4b2;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.compare-value {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
}
</style>
